import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import { ArrowForward, CheckCircle } from '@mui/icons-material';
import { apiService } from '../services/api';

const companyTypes = [
  'Private Equity Real Estate Fund',
  'UK Property Developer / Sponsor',
  'Real Estate Investment Trust (REIT)',
  'Commercial Debt Lender / Bank',
  'Asset Manager / Property Manager',
  'Real Estate Broker / Advisor',
];

const BetaSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [company, setCompany] = useState('');
  const [companyType, setCompanyType] = useState(companyTypes[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const device = typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop';

    if (!email || !email.includes('@')) {
      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupEmptyAttempt', { source: 'beta_form', device });
      }
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const companyDisplay = company ? `${company} (${companyType})` : companyType;
      await apiService.createBetaSignup({
        email,
        first_name: firstName,
        company: companyDisplay,
        signup_source: 'beta-section',
      });

      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupSubmitted', {
          source: 'beta_form',
          device,
          company_type: companyType,
          has_first_name: Boolean(firstName),
          has_company: Boolean(company),
          status: 'success',
        });
      }

      setEmail('');
      setFirstName('');
      setCompany('');
      setShowSuccess(true);
    } catch (error) {
      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupFailed', { source: 'beta_form', device, error: 'api_error' });
      }
      setErrorMessage('Failed to submit registration. Please try again.');
      console.error('Beta signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box id="beta-signup" sx={{ py: 12, bgcolor: '#f8fafc', position: 'relative' }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left Column */}
          <Grid item xs={12} lg={5}>
            <Box>
              <Chip
                label="PRIVATE BETA INVITATION"
                sx={{
                  bgcolor: 'rgba(4, 120, 87, 0.08)',
                  border: '1px solid rgba(4, 120, 87, 0.2)',
                  color: '#047857',
                  px: 1,
                  py: 0.5,
                  mb: 2,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: '#0f172a',
                  fontSize: { xs: '2rem', md: '2.75rem' },
                }}
              >
                Experience the Next Era of UK Real Estate Modelling
              </Typography>
              <Typography variant="body1" sx={{ color: '#475569', mb: 4, fontSize: '1.1rem' }}>
                Join an exclusive group of UK institutional investors, underwriters, and asset managers transforming their financial workflow.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {[
                  'Automated 1D & 2D sensitivity matrices',
                  'Instant UK macro interest rate & yield stress testing',
                  'Centralized model version control & visual diffing',
                  'Audit-ready cashflows & full revision history',
                ].map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle sx={{ color: '#047857', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Form */}
          <Grid item xs={12} lg={7}>
            <Card
              sx={{
                p: { xs: 3, md: 5 },
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.08)',
                borderRadius: 3.5,
              }}
            >
              <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 800, mb: 1 }}>
                Apply for Early Access
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', mb: 4 }}>
                Limited onboarding spots available for Q4 2026 UK release.
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name (Optional)"
                      variant="outlined"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Organization Category"
                      variant="outlined"
                      value={companyType}
                      onChange={(e) => setCompanyType(e.target.value)}
                    >
                      {companyTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isLoading}
                      endIcon={!isLoading && <ArrowForward />}
                      sx={{
                        py: 1.8,
                        fontSize: '1rem',
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Submit Beta Access Request'}
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {errorMessage && (
                <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'error.main' }}>
                  {errorMessage}
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ bgcolor: '#047857', color: '#ffffff', fontWeight: 700 }}>
          Registration successful! Our team will contact you with your private access key.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BetaSignup;
